import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import IsEmpty from 'lodash/isEmpty';

import CalendarPicker from '../../../components/CalendarPicker';
import BillingAddress from '../../../components/V2/BillingAddress';
import DeliveryDay from '../UserManagementPage/DeliveryDay';
import ProgramDetails from './components/ProgramDetails';
import ErrorModal from 'core/ui/components/V2/ErrorModal';
import InfoModal from 'core/ui/components/V2/InfoModal';
import { FETCH_USER_DELIVERY_SKIPS, GET_PROGRAM_DETAIL } from 'core/api/apollo/queries';
import { CREATE_DELIVERY_SKIPS, DESTROY_DELIVERY_SKIPS } from 'core/api/apollo/mutations';
import { NavigationHeader } from 'onramp/ui/components';
import { Button, Box, Flex, Hr, Text } from 'core/ui/atoms';
import { mySettingsPath } from 'onramp/api/navigation';
import SplitPageSidebar from 'core/ui/components/SplitPageSidebar';
import ResponsiveView from 'core/ui/components/ResponsiveView';

const MyProgramPage = () => {
  const history = useHistory();
  const [errorMsg, setErrorMsg] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const { data: { fetchUserDeliverySkips = [] } = [], refetch: refetchSkips } = useQuery(FETCH_USER_DELIVERY_SKIPS);

  const { loading: programLoading, error: programError, data: { getProgramDetail = {} } = {} } = useQuery(GET_PROGRAM_DETAIL);

  const [createDeliverySkips] = useMutation(CREATE_DELIVERY_SKIPS, {
    onCompleted: (data) => {
      // eslint-disable-next-line no-console
      console.log('Swap request created successfully', data);
      if (data?.createDeliverySkips?.message) {
        setInfoMessage(data?.createDeliverySkips);
        return;
      }
      const errorMessage = data?.createDeliverySkips?.errors[0]?.message;
      setErrorMsg(errorMessage);
    },
    onError: (error) => {
      console.error('Error deleting swap request', error);
      setErrorMsg('An unexpected error occurred while creating the swap request.');
    },
  });

  const [destroyDeliverySkips] = useMutation(DESTROY_DELIVERY_SKIPS, {
    onCompleted: (data) => {
      // eslint-disable-next-line no-console
      console.log('Swap request deleted successfully', data);
    },
    onError: (error) => {
      console.error('Error deleting swap request', error);
    },
  });

  const onSettingsNavigate = () => {
    history.push(mySettingsPath());
  };

  const isSubscription = getProgramDetail?.subscriptionActive;
  const daysPerWeek = getProgramDetail?.programBundle?.schedule?.daysOfWeek?.length;

  const RenderSideBar = () => (
    <CalendarContainer>
      <Title>My Calendar</Title>
      <SubTitle>
        Select a week to skip your program. If you'd like to make changes later,
        unskip that week first to edit your order.
      </SubTitle>
      <CalendarPicker
        daysPerWeek={daysPerWeek}
        refetchSkips={refetchSkips}
        deliverySkips={fetchUserDeliverySkips}
        createDeliverySkips={createDeliverySkips}
        destroyDeliverySkips={destroyDeliverySkips}
        setErrorMsg={setErrorMsg}
        setInfoMessage={setInfoMessage}
      />
    </CalendarContainer>
  );

  const RenderHeader = ({ isDesktop }) => (
    <ResponsiveView desktop={isDesktop}>
      <NavigationHeader title='My Program' />
      <Hr />
    </ResponsiveView>
  );

  RenderHeader.propTypes = {
    isDesktop: PropTypes.bool,
  };

  return (
    <Box>
      <RenderHeader isDesktop={true}  />
      <SplitPageSidebar sidebarWidth='40%' sidebarView={<RenderSideBar />}>
        <RenderHeader isDesktop={false} />
        <Content>
          <Title>My Program Details</Title>
          <ProgramDetails
            getProgramDetail={getProgramDetail}
            programLoading={programLoading}
            programError={programError}
          />
          {isSubscription && <FlexRowContainer direction='row' alignItems='center' justifyContent='center'>
            <SubscriptionButton btn-outline onClick={onSettingsNavigate}>
              <Text>MANAGE MY SUBSCRIPTION</Text>
            </SubscriptionButton>
          </FlexRowContainer>}
          <Hr />
          <ResponsiveView desktop={false}>
            <CalendarContainer>
              <Title>My Calendar</Title>
              <SubTitle>
                Select a week to skip your program. If you'd like to make changes later,
                unskip that week first to edit your order.
              </SubTitle>
              <CalendarPicker
                daysPerWeek={daysPerWeek}
                refetchSkips={refetchSkips}
                deliverySkips={fetchUserDeliverySkips}
                createDeliverySkips={createDeliverySkips}
                destroyDeliverySkips={destroyDeliverySkips}
                setErrorMsg={setErrorMsg}
                setInfoMessage={setInfoMessage}
              />
            </CalendarContainer>
            <Hr />
          </ResponsiveView>
          <AddressContainer>
            <Title>Default Delivery Address</Title>
            <BillingAddress />
          </AddressContainer>
          {!IsEmpty(getProgramDetail) &&
            <ResponsiveView desktop={true}>
              <DeliveryDay isDefault={true} />
            </ResponsiveView>
          }
        </Content>
        {!IsEmpty(getProgramDetail) &&
          <ResponsiveView desktop={false}>
            <DeliveryDay />
          </ResponsiveView>
        }
      </SplitPageSidebar>
      {errorMsg && <ErrorModal
        title="Can't Skip This Week"
        description={errorMsg}
        ctaText='OK'
        onCtaClick={() => setErrorMsg(null)}
        onClose={() => setErrorMsg(null)}
      />}
      {infoMessage?.message && <InfoModal
        title='Week Skipped'
        description={infoMessage?.message}
        extraMessage={infoMessage?.hasPaidMealSwaps &&
          "Since you'd previously swapped items for this week, any amount charged for these items will be refunded back to your card and any amount credited for these items will be removed from your account."
        }
        ctaText='OK'
        onCtaClick={() => setInfoMessage(null)}
        onClose={() => setInfoMessage(null)}
      />}
    </Box>
  );
};

const FlexRowContainer = styled(Flex)`
  margin-top: 36px;
  margin-bottom: 44px;
`;

const SubscriptionButton = styled(Button)`
  width: 320px;
  height: 43px;
  padding: 10px 32px;
  gap: 10px;
  border-radius: 4px;
  &:hover {
    p {
      color: white;
    }
  }
  p {
    margin: 0px;
    font-size: 12px;
    text-transform: uppercase;
    font-family: ${({ theme }) => theme.typography.familyPoppins};
    color: ${(p) => p.theme.colors.primaryV2};
  }
`;

const AddressContainer = styled(Box)`
`;

const SubTitle = styled(Text)`
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  text-align: center;
  margin-top: 0px;
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  color: ${(p) => p.theme.colors.primaryV2};
`;

const CalendarContainer = styled(Box)`
  padding-right: 100px;
  ${(p) => p.theme.max('sm')`
    margin-bottom: 30px;
    padding-left: 0px;
    padding-right: 0px;
  `}
`;

const Content = styled(Box)`
  padding-left: 16px;
  padding-right: 16px;
  width: 80%;
  ${(p) => p.theme.max('sm')`
    width: 100%;
  `}
`;

const Title = styled(Text)`
  text-align: center;
  font-size: 22px;
  font-weight: 400;
  line-height: 28px;
  font-family: ${({ theme }) => theme.typography.familyLarken};
  color: ${(p) => p.theme.colors.primaryV2};
`;

export default MyProgramPage;
